import React from 'react';
import { Link } from '../i18n';
import { Email, Tabela } from './pecas';

/**
 * Política de Privacidade, em pt, en e es. Vale o inglês; pt e es são tradução
 * de conveniência, e a MolduraJuridica diz isso a quem lê.
 *
 * Descreve o comportamento real do sistema, levantado do código: colunas de
 * `users` e `crawled_pages`; `services/seoSummary.js` e
 * `services/relevanciaDeTermos.js` para o que vai à Anthropic;
 * `services/googleIntegracoes.js` para as permissões do Google;
 * `jobs/cleanupData.js` para a retenção; `middleware/auth.js` e
 * `routes/auth.js` para os cookies; `public/index.html` e
 * `components/AvisoDeCookies.js` para o Google Analytics. Mudou o sistema, muda
 * o texto e a data.
 *
 * As seções ficam num array só, com o mesmo `id` nos três idiomas.
 */

const permissoes = (colunas, usos) => (
  <Tabela
    colunas={colunas}
    linhas={[
      ['Google Analytics', <code>analytics.readonly</code>, usos[0]],
      ['Search Console', <code>webmasters.readonly</code>, usos[1]],
      ['YouTube Analytics', <code>yt-analytics.readonly</code>, usos[2]],
      ['YouTube', <code>youtube.readonly</code>, usos[3]]
    ]}
  />
);

const cookies = (colunas, [sessao, dias, estado, minutos, analytics, anos]) => (
  <Tabela
    colunas={colunas}
    linhas={[
      [<code>sp_session</code>, sessao, <span className="num">{dias}</span>],
      [<code>sp_oauth_state</code>, estado, <span className="num">{minutos}</span>],
      [<code>_ga</code>, analytics, <span className="num">{anos}</span>],
      [<code>_ga_G3RV63LMBF</code>, analytics, <span className="num">{anos}</span>]
    ]}
  />
);

export const PRIVACIDADE = {
  atualizadoEm: '2026-10-28',
  titulo: { pt: 'Política de Privacidade', en: 'Privacy Policy', es: 'Política de Privacidad' },
  lead: {
    pt: 'O que o Silent Puma guarda, quem mais trata esses dados, por quanto tempo e como você apaga tudo.',
    en: 'What Silent Puma stores, who else processes it, how long we keep it, and how you delete it all.',
    es: 'Qué guarda Silent Puma, quién más trata esos datos, por cuánto tiempo y cómo lo borras todo.'
  },
  atualizado: { pt: 'Atualizada em', en: 'Last updated', es: 'Actualizada el' },
  provisoria: {
    pt: 'Versão provisória: descreve o que o sistema faz hoje, mas ainda não passou por advogado e não é um texto jurídico final. Se algo aqui pesa numa decisão sua, escreva antes para',
    en: 'Provisional version: this document describes how Silent Puma actually handles data today, but it has not been reviewed by a lawyer and is not a final legal text. If anything here matters to a decision you are making, write to us first at',
    es: 'Versión provisional: describe lo que el sistema hace hoy, pero aún no la revisó un abogado y no es un texto legal definitivo. Si algo aquí pesa en una decisión tuya, escribe antes a'
  },
  resumo: [
    {
      ancora: 'operadores',
      texto: {
        pt: 'Não vendemos seus dados nem os passamos a anunciantes.',
        en: "We don't sell your data or pass it to advertisers.",
        es: 'No vendemos tus datos ni los pasamos a anunciantes.'
      }
    },
    {
      ancora: 'ia',
      texto: {
        pt: 'A IA só recebe dados quando você pede uma análise.',
        en: 'The AI only receives data when you ask for an analysis.',
        es: 'La IA solo recibe datos cuando pides un análisis.'
      }
    },
    {
      ancora: 'google',
      texto: {
        pt: 'As integrações com o Google só leem, e você revoga quando quiser.',
        en: 'Google integrations are read-only, and you can revoke them anytime.',
        es: 'Las integraciones con Google solo leen, y puedes revocarlas cuando quieras.'
      }
    },
    {
      ancora: 'cookies',
      texto: {
        pt: 'Os cookies do login e, só se você aceitar, os do Google Analytics. Nenhum de anúncio.',
        en: 'Sign-in cookies, plus Google Analytics only if you accept. None for ads.',
        es: 'Las cookies del inicio de sesión y, solo si aceptas, las de Google Analytics. Ninguna de anuncios.'
      }
    },
    {
      ancora: 'retencao',
      texto: {
        pt: 'Apagar a conta apaga tudo, na hora e sem volta.',
        en: 'Deleting your account deletes everything, immediately and for good.',
        es: 'Borrar la cuenta borra todo, al instante y sin vuelta atrás.'
      }
    }
  ],
  secoes: [
    {
      id: 'escopo',
      titulo: { pt: 'O que esta política cobre', en: 'What this policy covers', es: 'Qué cubre esta política' },
      corpo: {
        pt: (
          <>
            <p>
              Esta política cobre o aplicativo web do Silent Puma e a API dele. Ela diz o que guardamos, quem mais
              trata esses dados, por quanto tempo eles ficam e como você os remove.
            </p>
            <p>
              Ela não cobre os sites que você escolhe analisar. Eles são seus ou dos seus clientes, e quem os visita
              está coberto pelas políticas deles.
            </p>
          </>
        ),
        en: (
          <>
            <p>
              This policy covers the Silent Puma web application and its API. It explains what we store, who else
              processes it, how long we keep it, and how you remove it.
            </p>
            <p>
              It does not cover the websites you choose to analyze. Those belong to you or to your clients, and their
              own policies apply to their visitors.
            </p>
          </>
        ),
        es: (
          <>
            <p>
              Esta política cubre la aplicación web de Silent Puma y su API. Explica qué guardamos, quién más trata
              esos datos, cuánto tiempo los conservamos y cómo los eliminas.
            </p>
            <p>
              No cubre los sitios que eliges analizar. Son tuyos o de tus clientes, y a sus visitantes se les aplican
              las políticas de esos sitios.
            </p>
          </>
        )
      }
    },
    {
      id: 'coleta',
      titulo: { pt: 'O que coletamos', en: 'What we collect', es: 'Qué recopilamos' },
      corpo: {
        pt: (
          <>
            <p>
              <b>Conta.</b> Nome, e-mail e uma senha, guardada só como hash e nunca em forma legível, ou o
              identificador da sua conta Google, se você entra com o Google. Uma foto de perfil, se você enviar uma.
              Se a conta é de uma empresa, o nome dela, a faixa de número de funcionários e se ela é uma agência.
            </p>
            <p>
              <b>Cobrança.</b> Os identificadores de cliente e de assinatura no Stripe, e o seu plano atual. Números
              de cartão nunca chegam aos nossos servidores: os dados de pagamento são digitados na página de checkout
              do próprio Stripe e ficam com o Stripe.
            </p>
            <p>
              <b>Uso.</b> Projetos e páginas consumidos no período atual, créditos de IA concedidos e gastos, e a data
              da última renovação. Eles existem para aplicar os limites do seu plano.
            </p>
            <p>
              <b>Dados de análise.</b> O que o rastreador coleta dos sites e canais do YouTube que você cadastra,
              descrito a seguir.
            </p>
          </>
        ),
        en: (
          <>
            <p>
              <b>Account.</b> Name, email address, and either a password, stored only as a hash and never in readable
              form, or a Google account identifier if you sign in with Google. An avatar image, if you upload one.
              If the account belongs to a company, its name, its employee-count range and whether it is an agency.
            </p>
            <p>
              <b>Billing.</b> A Stripe customer identifier, a subscription identifier and your current plan. Card
              numbers never reach our servers: payment details are entered on Stripe’s own checkout page and stay
              with Stripe.
            </p>
            <p>
              <b>Usage.</b> Projects and pages consumed in the current period, AI credits granted and spent, and the
              date of the last reset. These exist to enforce your plan’s limits.
            </p>
            <p>
              <b>Analysis data.</b> Whatever the crawler collects from the sites and YouTube channels you register,
              described next.
            </p>
          </>
        ),
        es: (
          <>
            <p>
              <b>Cuenta.</b> Nombre, correo y una contraseña, guardada solo como hash y nunca en forma legible, o el
              identificador de tu cuenta de Google, si inicias sesión con Google. Una foto de perfil, si subes una.
              Si la cuenta es de una empresa, su nombre, el rango de número de empleados y si es una agencia.
            </p>
            <p>
              <b>Facturación.</b> Los identificadores de cliente y de suscripción en Stripe, y tu plan actual. Los
              números de tarjeta nunca llegan a nuestros servidores: los datos de pago se escriben en la página de
              checkout del propio Stripe y se quedan con Stripe.
            </p>
            <p>
              <b>Uso.</b> Proyectos y páginas consumidos en el período actual, créditos de IA otorgados y gastados, y
              la fecha de la última renovación. Existen para aplicar los límites de tu plan.
            </p>
            <p>
              <b>Datos de análisis.</b> Lo que el rastreador recopila de los sitios y canales de YouTube que
              registras, descrito a continuación.
            </p>
          </>
        )
      }
    },
    {
      id: 'rastreador',
      titulo: { pt: 'O que o rastreador coleta', en: 'What the crawler collects', es: 'Qué recopila el rastreador' },
      corpo: {
        pt: (
          <>
            <p>
              Quando você inicia uma execução, buscamos as páginas a partir do endereço cadastrado e guardamos, de cada
              uma:
            </p>
            <ul className="nh-lista">
              <li>URL, título, meta description e meta keywords</li>
              <li>Código de status HTTP, tipo de conteúdo, tempo de carregamento e tamanho</li>
              <li>Headings, contagens de links internos e externos e de imagens</li>
              <li>A lista de links encontrados na página</li>
            </ul>
            <p>
              Nos projetos do YouTube, lemos metadados públicos do canal e dos vídeos pela YouTube Data API: títulos,
              descrições, tags e as contagens de visualizações, curtidas e comentários.
            </p>
            <p>
              Não guardamos uma cópia inteira das páginas e não coletamos nada sobre os visitantes dos sites que
              rastreamos.
            </p>
          </>
        ),
        en: (
          <>
            <p>When you start a run, we fetch pages from the address you registered and store, for each page:</p>
            <ul className="nh-lista">
              <li>URL, page title, meta description and meta keywords</li>
              <li>HTTP status code, content type, load time and page size</li>
              <li>Heading tags, counts of internal and external links, image counts</li>
              <li>The list of links found on the page</li>
            </ul>
            <p>
              For YouTube projects we read public channel and video metadata through the YouTube Data API: titles,
              descriptions, tags, and view, like and comment counts.
            </p>
            <p>
              We do not keep a full copy of the pages, and we do not collect anything about the visitors of the sites
              we crawl.
            </p>
          </>
        ),
        es: (
          <>
            <p>
              Cuando inicias una ejecución, buscamos las páginas a partir de la dirección registrada y guardamos, de
              cada una:
            </p>
            <ul className="nh-lista">
              <li>URL, título, meta description y meta keywords</li>
              <li>Código de estado HTTP, tipo de contenido, tiempo de carga y tamaño</li>
              <li>Encabezados, cantidad de enlaces internos y externos y de imágenes</li>
              <li>La lista de enlaces encontrados en la página</li>
            </ul>
            <p>
              En los proyectos de YouTube, leemos metadatos públicos del canal y de los videos con la YouTube Data API:
              títulos, descripciones, etiquetas y las cantidades de vistas, me gusta y comentarios.
            </p>
            <p>
              No guardamos una copia completa de las páginas ni recopilamos nada sobre los visitantes de los sitios que
              rastreamos.
            </p>
          </>
        )
      }
    },
    {
      id: 'ia',
      titulo: { pt: 'Análise de IA', en: 'AI analysis', es: 'Análisis de IA' },
      corpo: {
        pt: (
          <>
            <p>
              Quando você pede uma análise de IA, enviamos à Anthropic um resumo da execução, e ela devolve as
              recomendações priorizadas que você vê na tela. O resumo é agregado: quantas páginas têm cada problema,
              com até 8 endereços de exemplo por problema, uma amostra de até 200 endereços rastreados e as instruções
              que você escrever para a análise. Nos canais do YouTube, os exemplos são títulos de vídeos, e o resumo
              também traz os títulos dos vídeos com melhor e pior desempenho, com as visualizações e a taxa de
              engajamento de cada um.
            </p>
            <p>
              Quando você pede à IA que avalie a relevância dos seus termos de busca, enviamos também, para cada par de
              página e termo: o termo de busca que veio do Search Console, o caminho da página, o título, a meta
              description e até três H1.
            </p>
            <p>
              Nenhuma das duas acontece sozinha. As duas só rodam quando você pede, e gastam créditos de IA do seu
              plano.
            </p>
          </>
        ),
        en: (
          <>
            <p>
              When you ask for an AI analysis, we send a summary of the run to Anthropic, which returns the
              prioritized recommendations you see on screen. The summary is aggregated: how many pages have each
              problem, with up to 8 example URLs per problem, a sample of up to 200 crawled URLs, and any instructions
              you write for the analysis. For YouTube channels, the examples are video titles, and the summary also
              lists the titles of your best and worst performing videos with their view counts and engagement rates.
            </p>
            <p>
              When you ask the AI to check the relevance of your search terms, we also send, for each page and term
              pair: the search term from Search Console, the page path, its title, its meta description and up to
              three H1 headings.
            </p>
            <p>
              Neither happens on its own. Both run only when you ask for them, and they spend AI credits from your
              plan.
            </p>
          </>
        ),
        es: (
          <>
            <p>
              Cuando pides un análisis de IA, enviamos a Anthropic un resumen de la ejecución, y devuelve las
              recomendaciones priorizadas que ves en pantalla. El resumen es agregado: cuántas páginas tienen cada
              problema, con hasta 8 direcciones de ejemplo por problema, una muestra de hasta 200 direcciones
              rastreadas y las instrucciones que escribas para el análisis. En los canales de YouTube, los ejemplos son
              títulos de videos, y el resumen también incluye los títulos de los videos con mejor y peor rendimiento,
              con las vistas y la tasa de interacción de cada uno.
            </p>
            <p>
              Cuando pides a la IA que evalúe la relevancia de tus términos de búsqueda, también enviamos, para cada
              par de página y término: el término de búsqueda que vino de Search Console, la ruta de la página, el
              título, la meta description y hasta tres H1.
            </p>
            <p>
              Ninguno de los dos ocurre por sí solo. Ambos se ejecutan solo cuando los pides, y gastan créditos de IA
              de tu plan.
            </p>
          </>
        )
      }
    },
    {
      id: 'google',
      titulo: { pt: 'Integrações com o Google', en: 'Google integrations', es: 'Integraciones con Google' },
      corpo: {
        pt: (
          <>
            <p>
              Conectar o Google Analytics, o Search Console ou o YouTube Analytics é opcional. Quando você conecta,
              pedimos só as permissões de leitura abaixo, e nada além delas:
            </p>
            {permissoes(
              ['Integração', 'Permissão', 'Para quê'],
              [
                'Ler os relatórios',
                'Ler as propriedades verificadas',
                'Ler os relatórios de desempenho do canal que você escolher',
                'Listar os canais que a sua conta administra, para você escolher um e para conferirmos que é o mesmo que o projeto rastreia'
              ]
            )}
            <p>
              Do YouTube lemos medidas de audiência: visualizações, tempo assistido, retenção, inscritos ganhos e os
              termos que as pessoas digitaram no YouTube antes de chegar aos seus vídeos. São números agregados do seu
              canal. Não recebemos, e não temos como pedir, a identidade de quem assistiu.
            </p>
            <p>
              Não podemos alterar nada nessas contas, e você revoga o acesso quando quiser nas configurações da sua
              conta Google, sem precisar nos pedir.
            </p>
          </>
        ),
        en: (
          <>
            <p>
              Connecting Google Analytics, Search Console or YouTube Analytics is optional. When you do, we request
              the read-only permissions below and nothing else:
            </p>
            {permissoes(
              ['Integration', 'Permission', 'What for'],
              [
                'Read-only reporting access',
                'Read-only access to your verified properties',
                'Read-only access to the performance reports of the channel you choose',
                'Read-only access to the list of channels your account manages, which we use to let you pick one and to check that it is the same channel the project crawls'
              ]
            )}
            <p>
              For YouTube we read audience measurements: views, watch time, retention, subscribers gained, and the
              search terms people typed on YouTube before reaching your videos. These are aggregate figures about your
              channel. We do not receive, and cannot request, the identity of individual viewers.
            </p>
            <p>
              We cannot change anything in those accounts, and you can revoke access at any time from your Google
              account settings, without asking us.
            </p>
          </>
        ),
        es: (
          <>
            <p>
              Conectar Google Analytics, Search Console o YouTube Analytics es opcional. Cuando lo haces, pedimos solo
              los permisos de lectura de abajo, y nada más:
            </p>
            {permissoes(
              ['Integración', 'Permiso', 'Para qué'],
              [
                'Leer los informes',
                'Leer las propiedades verificadas',
                'Leer los informes de rendimiento del canal que elijas',
                'Listar los canales que administra tu cuenta, para que elijas uno y para comprobar que es el mismo que rastrea el proyecto'
              ]
            )}
            <p>
              De YouTube leemos medidas de audiencia: vistas, tiempo de reproducción, retención, suscriptores ganados y
              los términos que la gente escribió en YouTube antes de llegar a tus videos. Son cifras agregadas de tu
              canal. No recibimos, ni podemos pedir, la identidad de quien vio los videos.
            </p>
            <p>
              No podemos cambiar nada en esas cuentas, y puedes revocar el acceso cuando quieras en la configuración de
              tu cuenta de Google, sin pedírnoslo.
            </p>
          </>
        )
      }
    },
    {
      id: 'operadores',
      titulo: { pt: 'Quem mais trata seus dados', en: 'Who else processes your data', es: 'Quién más trata tus datos' },
      corpo: {
        pt: (
          <>
            <p>Contamos com estes fornecedores para operar o serviço:</p>
            <ul className="nh-lista">
              <li>
                <b>DigitalOcean</b>: hospedagem e o banco de dados onde seus dados ficam
              </li>
              <li>
                <b>Stripe</b>: assinaturas e pagamentos
              </li>
              <li>
                <b>Anthropic</b>: a análise de IA descrita acima
              </li>
              <li>
                <b>Google</b>: login, as integrações opcionais, a YouTube Data API e, só se você aceitar os cookies, o
                Google Analytics, que mede as visitas ao site
              </li>
              <li>
                <b>DataForSEO</b>: o volume de busca dos termos que o Search Console do seu site informa. Enviamos só
                o texto das buscas
              </li>
              <li>
                <b>Resend</b>: e-mails transacionais, como a redefinição de senha
              </li>
            </ul>
            <p>Não vendemos seus dados e não os compartilhamos com anunciantes.</p>
          </>
        ),
        en: (
          <>
            <p>We rely on these providers to run the service:</p>
            <ul className="nh-lista">
              <li>
                <b>DigitalOcean</b>: hosting and the database where your data lives
              </li>
              <li>
                <b>Stripe</b>: subscriptions and payments
              </li>
              <li>
                <b>Anthropic</b>: the AI analysis described above
              </li>
              <li>
                <b>Google</b>: sign-in, the optional integrations, the YouTube Data API and, only if you accept the
                cookies, Google Analytics, which measures visits to the site
              </li>
              <li>
                <b>DataForSEO</b>: search volume for the terms that Search Console reports for your site. We send only
                the text of the searches
              </li>
              <li>
                <b>Resend</b>: transactional email, such as password resets
              </li>
            </ul>
            <p>We do not sell your data, and we do not share it with advertisers.</p>
          </>
        ),
        es: (
          <>
            <p>Contamos con estos proveedores para operar el servicio:</p>
            <ul className="nh-lista">
              <li>
                <b>DigitalOcean</b>: alojamiento y la base de datos donde están tus datos
              </li>
              <li>
                <b>Stripe</b>: suscripciones y pagos
              </li>
              <li>
                <b>Anthropic</b>: el análisis de IA descrito arriba
              </li>
              <li>
                <b>Google</b>: inicio de sesión, las integraciones opcionales, la YouTube Data API y, solo si aceptas las
                cookies, Google Analytics, que mide las visitas al sitio
              </li>
              <li>
                <b>DataForSEO</b>: el volumen de búsqueda de los términos que informa el Search Console de tu sitio.
                Enviamos solo el texto de las búsquedas
              </li>
              <li>
                <b>Resend</b>: correos transaccionales, como el restablecimiento de contraseña
              </li>
            </ul>
            <p>No vendemos tus datos ni los compartimos con anunciantes.</p>
          </>
        )
      }
    },
    {
      id: 'cookies',
      titulo: { pt: 'Cookies', en: 'Cookies', es: 'Cookies' },
      corpo: {
        pt: (
          <>
            <p>
              O Silent Puma usa um cookie de sessão, que mantém você conectado. Ele é marcado como <code>httpOnly</code>,
              então scripts da página não conseguem lê-lo. Durante o login com o Google usamos também um cookie de vida
              curta, que protege o próprio login contra adulteração.
            </p>
            {cookies(
              ['Cookie', 'Para quê', 'Dura'],
              [
                'Mantém você conectado. Scripts da página não o leem.',
                '7 dias',
                'Protege o login com o Google contra adulteração.',
                '10 minutos',
                'Google Analytics, só se você aceitar. Distingue um visitante de outro, sem dizer quem é.',
                '2 anos'
              ]
            )}
            <p>
              Os cookies do Google Analytics só existem se você aceitar no aviso de cookies: antes disso, nem o Google
              Tag Manager nem o Google Analytics são carregados, e nada é enviado ao Google. Com a sua permissão, eles
              contam visitas e páginas vistas, e registram quando alguém envia o formulário de contato, cria uma conta ou
              entra — nunca com o seu nome, e-mail ou mensagem.
            </p>
            <p>
              A sua escolha fica guardada no seu navegador, no armazenamento local (<code>sp-cookies</code>). Para mudar
              de ideia, use o link Cookies no rodapé; recusar depois de ter aceitado apaga os cookies do Google
              Analytics. Não há cookies de publicidade.
            </p>
          </>
        ),
        en: (
          <>
            <p>
              Silent Puma sets one session cookie, which keeps you signed in. It is marked <code>httpOnly</code>, so
              scripts running in the page cannot read it. During a Google sign-in we also set a short-lived cookie that
              protects the sign-in itself from being tampered with.
            </p>
            {cookies(
              ['Cookie', 'What for', 'Lasts'],
              [
                'Keeps you signed in. Scripts on the page cannot read it.',
                '7 days',
                'Protects the Google sign-in from being tampered with.',
                '10 minutes',
                'Google Analytics, only if you accept. Tells one visitor from another without saying who they are.',
                '2 years'
              ]
            )}
            <p>
              The Google Analytics cookies only exist if you accept them in the cookie notice: until then, neither
              Google Tag Manager nor Google Analytics is loaded, and nothing is sent to Google. With your permission,
              they count visits and page views, and record when someone sends the contact form, creates an account or
              signs in — never with your name, email or message.
            </p>
            <p>
              Your choice is stored in your browser’s local storage (<code>sp-cookies</code>). To change your mind, use
              the Cookies link in the footer; declining after having accepted deletes the Google Analytics cookies. There
              are no advertising cookies.
            </p>
          </>
        ),
        es: (
          <>
            <p>
              Silent Puma usa una cookie de sesión, que mantiene tu sesión iniciada. Está marcada como{' '}
              <code>httpOnly</code>, así que los scripts de la página no pueden leerla. Durante el inicio de sesión con
              Google usamos también una cookie de corta duración, que protege el propio inicio de sesión contra
              manipulaciones.
            </p>
            {cookies(
              ['Cookie', 'Para qué', 'Dura'],
              [
                'Mantiene tu sesión iniciada. Los scripts de la página no la leen.',
                '7 días',
                'Protege el inicio de sesión con Google contra manipulaciones.',
                '10 minutos',
                'Google Analytics, solo si aceptas. Distingue a un visitante de otro, sin decir quién es.',
                '2 años'
              ]
            )}
            <p>
              Las cookies de Google Analytics solo existen si las aceptas en el aviso de cookies: antes de eso, ni Google
              Tag Manager ni Google Analytics se cargan, y no se envía nada a Google. Con tu permiso, cuentan visitas y
              páginas vistas, y registran cuando alguien envía el formulario de contacto, crea una cuenta o inicia sesión
              — nunca con tu nombre, correo o mensaje.
            </p>
            <p>
              Tu elección queda guardada en tu navegador, en el almacenamiento local (<code>sp-cookies</code>). Para
              cambiar de opinión, usa el enlace Cookies del pie de página; rechazar después de haber aceptado borra las
              cookies de Google Analytics. No hay cookies de publicidad.
            </p>
          </>
        )
      }
    },
    {
      id: 'retencao',
      titulo: { pt: 'Por quanto tempo guardamos', en: 'How long we keep it', es: 'Cuánto tiempo los guardamos' },
      corpo: {
        pt: (
          <>
            <p>
              Os dados de análise vencem conforme o seu plano, e uma rotina diária apaga o que passou do limite. A
              retenção do seu plano aparece na página de <Link to="/plans">Planos</Link> e acima da sua lista de
              execuções.
            </p>
            <p>
              Os dados da conta ficam enquanto a conta existir. Apagar a conta remove tudo junto, com cada projeto,
              execução, página coletada e análise, e cancela qualquer assinatura ativa. A exclusão é imediata e não
              tem volta: não existe prazo de recuperação.
            </p>
          </>
        ),
        en: (
          <>
            <p>
              Analysis data expires according to your plan, and a daily job deletes whatever is past the limit. The
              retention window for your plan is shown on the <Link to="/plans">Plans</Link> page and above your runs
              list.
            </p>
            <p>
              Account data is kept while the account exists. Deleting your account removes it together with every
              project, run, collected page and analysis, and cancels any active subscription. That deletion is
              immediate and cannot be undone: there is no recovery window.
            </p>
          </>
        ),
        es: (
          <>
            <p>
              Los datos de análisis vencen según tu plan, y una tarea diaria borra lo que pasó del límite. La retención
              de tu plan aparece en la página de <Link to="/plans">Planes</Link> y encima de tu lista de ejecuciones.
            </p>
            <p>
              Los datos de la cuenta se guardan mientras la cuenta exista. Borrar la cuenta elimina todo junto, con
              cada proyecto, ejecución, página recopilada y análisis, y cancela cualquier suscripción activa. El
              borrado es inmediato y no tiene vuelta atrás: no hay plazo de recuperación.
            </p>
          </>
        )
      }
    },
    {
      id: 'direitos',
      titulo: { pt: 'Seus direitos', en: 'Your rights', es: 'Tus derechos' },
      corpo: {
        pt: (
          <>
            <ul className="nh-lista">
              <li>Ver e corrigir seu nome e e-mail nas configurações da conta</li>
              <li>Exportar os dados coletados em CSV, por projeto ou por execução</li>
              <li>Apagar a conta e tudo o que ela tem, nas configurações da conta</li>
              <li>Revogar as integrações com o Google a qualquer momento</li>
            </ul>
            <p>
              Para o que a interface não cobre, como uma cópia dos seus dados em outro formato ou uma dúvida sobre como
              algo é tratado, escreva para <Email />.
            </p>
          </>
        ),
        en: (
          <>
            <ul className="nh-lista">
              <li>See and correct your name and email in account settings</li>
              <li>Export your collected data as CSV, per project or per run</li>
              <li>Delete your account and everything in it, from account settings</li>
              <li>Revoke the Google integrations at any time</li>
            </ul>
            <p>
              For anything the interface does not cover, such as a copy of your data in another format or a question
              about how something is processed, write to <Email />.
            </p>
          </>
        ),
        es: (
          <>
            <ul className="nh-lista">
              <li>Ver y corregir tu nombre y correo en la configuración de la cuenta</li>
              <li>Exportar los datos recopilados en CSV, por proyecto o por ejecución</li>
              <li>Borrar la cuenta y todo lo que contiene, en la configuración de la cuenta</li>
              <li>Revocar las integraciones con Google en cualquier momento</li>
            </ul>
            <p>
              Para lo que la interfaz no cubre, como una copia de tus datos en otro formato o una duda sobre cómo se
              trata algo, escribe a <Email />.
            </p>
          </>
        )
      }
    },
    {
      id: 'seguranca',
      titulo: { pt: 'Segurança', en: 'Security', es: 'Seguridad' },
      corpo: {
        pt: (
          <>
            <p>
              O tráfego é criptografado em trânsito, a conexão com o banco de dados é criptografada e tem o certificado
              verificado, as senhas são guardadas só como hash e o acesso a um projeto é conferido contra o dono dele a
              cada requisição.
            </p>
            <p>
              Nenhum sistema está fora de alcance. Se você acha que encontrou um problema de segurança, conte para nós
              em <Email /> e olhamos antes de qualquer outra coisa.
            </p>
          </>
        ),
        en: (
          <>
            <p>
              Traffic is encrypted in transit, the database connection is encrypted and certificate-verified,
              passwords are stored only as hashes, and access to a project is checked against its owner on every
              request.
            </p>
            <p>
              No system is beyond reach. If you believe you have found a security problem, tell us at <Email /> and we
              will look at it before anything else.
            </p>
          </>
        ),
        es: (
          <>
            <p>
              El tráfico está cifrado en tránsito, la conexión con la base de datos está cifrada y tiene el certificado
              verificado, las contraseñas se guardan solo como hash y el acceso a un proyecto se comprueba contra su
              dueño en cada solicitud.
            </p>
            <p>
              Ningún sistema está fuera de alcance. Si crees que encontraste un problema de seguridad, avísanos en{' '}
              <Email /> y lo revisaremos antes que cualquier otra cosa.
            </p>
          </>
        )
      }
    },
    {
      id: 'mudancas',
      titulo: { pt: 'Mudanças nesta política', en: 'Changes to this policy', es: 'Cambios en esta política' },
      corpo: {
        pt: (
          <p>
            Quando esta política muda, atualizamos a data no topo. Se a mudança afetar o tratamento dos seus dados de
            um jeito que você não esperaria, avisamos por e-mail em vez de deixar para você perceber.
          </p>
        ),
        en: (
          <p>
            When this policy changes we update the date at the top. If a change affects how your data is processed in
            a way you would not expect, we will tell you by email rather than leave it for you to notice.
          </p>
        ),
        es: (
          <p>
            Cuando esta política cambia, actualizamos la fecha de arriba. Si el cambio afecta el tratamiento de tus
            datos de una forma que no esperarías, te avisamos por correo en vez de dejar que lo descubras.
          </p>
        )
      }
    },
    {
      id: 'contato',
      titulo: { pt: 'Contato', en: 'Contact', es: 'Contacto' },
      corpo: {
        pt: (
          <p>
            Para qualquer assunto sobre privacidade ou seus dados, escreva para <Email />. Respondemos em até um dia
            útil.
          </p>
        ),
        en: (
          <p>
            For anything about privacy or your data, write to <Email />. We reply within one business day.
          </p>
        ),
        es: (
          <p>
            Para cualquier asunto sobre privacidad o tus datos, escribe a <Email />. Respondemos en un día hábil como
            máximo.
          </p>
        )
      }
    }
  ]
};
